import React, { useEffect, useRef, useState } from "react";
import AuthService from "../../services/AuthService";
import InscricaoService from "../../services/Inscricoes/InscricaoService";
import { initMercadoPago } from "@mercadopago/sdk-react";
import { MERCADOPAGO_PUBLIC_KEY, API_BASE_URL } from "../../services/config";
import CheckoutPage from "../checkout/CheckoutPage";
import NovoEventoService from "../../services/Eventos/NovoEventoService";
import {
    obterLoteAtivoEDisponivel,
    isFaixaEtaria60Mais,
    calcularValorComDesconto60Mais,
    formatCPF,
    unformatCPF,
    isValidCPF,
    formatPhone,
    calcularSubtotalParticipantes
} from "../../utils/loteUtils";
import { Button, Input, Select, Checkbox, Skeleton, Toast, Divider } from "../ui";
import { CheckCircle2, FileText, Gift, Ticket, Check, Plus, Trash2, User, Users, Info, AlertTriangle } from "lucide-react";

initMercadoPago(MERCADOPAGO_PUBLIC_KEY);

function InscricaoCorredor({ produto, categorias }) {
    const [userData, setUserData] = useState({});
    const [showForm, setShowForm] = useState(true);
    const [showEsqueleton, setShowEsqueleton] = useState(false);
    const [showPagamento, setShowPagamento] = useState(false);
    const [inscricaoCreated, setInscricaoCreated] = useState(null);
    const [hasVoucher, setHasVoucher] = useState(false);
    const [voucher, setVoucher] = useState("");
    const [errorMessage, setErrorMessage] = useState(null);
    const [loteAtivo, setLoteAtivo] = useState(
        (Array.isArray(produto?.lotes) ? obterLoteAtivoEDisponivel(produto.lotes) : null) || produto?.lote_ativo || null
    );
    const toast = useRef(null);

    const authservice = new AuthService();
    const inscricaoService = new InscricaoService();
    const eventoService = new NovoEventoService();

    // Estado do Titular
    const [jaInscritoTitular, setJaInscritoTitular] = useState(false);
    const [incluirTitular, setIncluirTitular] = useState(true);
    const [titularCategoria, setTitularCategoria] = useState(null);
    const [titularTamanhoBlusa, setTitularTamanhoBlusa] = useState(null);
    const [titularEquipe, setTitularEquipe] = useState("");

    // Estado de Terceiros: array de { id, nome_corredor, cpf_corredor, telefone_corredor, data_nascimento_corredor, categoria, tamanho_blusa, equipe_patrocinio }
    const [terceiros, setTerceiros] = useState([]);

    const blusas = [
        { Tamanho: 'P' },
        { Tamanho: 'PP' },
        { Tamanho: 'M' },
        { Tamanho: 'G' },
        { Tamanho: 'GG' },
        { Tamanho: 'GGG' }
    ];

    // Carrega dados do usuário logado e checa se ele já está inscrito nesta corrida
    useEffect(() => {
        const getUserData = async () => {
            const userId = sessionStorage.getItem('brv-user-id');
            if (!userId) return;

            try {
                const response = await authservice.getUserData(userId);
                if (response.ok) {
                    const dat = await response.json();
                    setUserData(dat);
                }

                // Verifica inscrições vigentes e pendentes para detectar se o titular já está inscrito
                if (produto?.id) {
                    const [resAtuais, resPendentes] = await Promise.all([
                        inscricaoService.getCorridasAtuaisUsuario(userId).catch(() => null),
                        inscricaoService.getCorridasPendentesUsuario(userId).catch(() => null)
                    ]);

                    let jaInscrito = false;

                    const checarLista = async (res) => {
                        if (res && res.ok) {
                            const list = await res.json();
                            if (Array.isArray(list)) {
                                const found = list.some(i => {
                                    const idCorrida = i.id_corrida?.id || i.id_corrida || i.corrida?.id || i.corrida;
                                    const isMesmaCorrida = String(idCorrida) === String(produto.id);
                                    // Titular é quem tem is_terceiro === false
                                    const isTitular = i.is_terceiro === false || i.is_terceiro === undefined || i.is_terceiro === null;
                                    const statusValido = i.status !== 'Cancelada' && i.status !== 'Cancelado';
                                    return isMesmaCorrida && isTitular && statusValido;
                                });
                                if (found) jaInscrito = true;
                            }
                        }
                    };

                    await checarLista(resAtuais);
                    if (!jaInscrito) await checarLista(resPendentes);

                    if (jaInscrito) {
                        setJaInscritoTitular(true);
                        setIncluirTitular(false);
                        // Adiciona 1 terceiro por padrão para facilitar para o usuário
                        setTerceiros([
                            {
                                id: Date.now(),
                                nome_corredor: "",
                                cpf_corredor: "",
                                telefone_corredor: "",
                                data_nascimento_corredor: "",
                                categoria: null,
                                tamanho_blusa: null,
                                equipe_patrocinio: ""
                            }
                        ]);
                    }
                }
            } catch (err) {
                console.log("Erro ao carregar dados do usuário ou inscrições:", err);
            }
        };
        getUserData();
    }, [produto?.id]);

    // Busca lote ativo e disponível
    useEffect(() => {
        if (produto?.id) {
            const fetchLoteAtivo = async () => {
                try {
                    let lotesList = [];
                    const resLotes = await eventoService.getLotesPorCorrida(produto.id);
                    if (resLotes.ok) {
                        const lts = await resLotes.json();
                        if (Array.isArray(lts) && lts.length > 0) {
                            lotesList = lts;
                        }
                    }

                    if (lotesList.length === 0 && Array.isArray(produto.lotes) && produto.lotes.length > 0) {
                        lotesList = produto.lotes;
                    }

                    if (lotesList.length > 0) {
                        const loteDisponivel = obterLoteAtivoEDisponivel(lotesList);
                        setLoteAtivo(loteDisponivel);
                        return;
                    }

                    const token = sessionStorage.getItem('brv-user-token');
                    const headers = token ? { 'Authorization': 'Token ' + token } : {};
                    const resAtivo = await fetch(`${API_BASE_URL}/core/lotes/corrida/${produto.id}/ativo/`, { headers });
                    if (resAtivo.ok) {
                        const lote = await resAtivo.json();
                        if (lote && (lote.vagas_disponiveis === undefined || lote.vagas_disponiveis === null || lote.vagas_disponiveis > 0)) {
                            setLoteAtivo(lote);
                            return;
                        }
                    }

                    setLoteAtivo(produto?.lote_ativo || null);
                } catch (err) {
                    console.error("Erro ao buscar lote ativo para inscrição:", err);
                    if (Array.isArray(produto?.lotes)) {
                        setLoteAtivo(obterLoteAtivoEDisponivel(produto.lotes));
                    } else if (produto?.lote_ativo) {
                        setLoteAtivo(produto.lote_ativo);
                    }
                }
            };
            fetchLoteAtivo();
        }
    }, [produto?.id]);

    const [pagamentoConfirmado, setPagamentoConfirmado] = useState(false);
    const [numCorredor, setNumCorredor] = useState(null);
    const [inscricoesConfirmadas, setInscricoesConfirmadas] = useState([]);

    // Polling de confirmação de pagamento para o lote
    useEffect(() => {
        let intervalId;
        if (showPagamento && !hasVoucher && !pagamentoConfirmado) {
            const checkStatus = async () => {
                try {
                    const userId = sessionStorage.getItem('brv-user-id');
                    if (!userId) return;

                    const res = await inscricaoService.getCorridasAtuaisUsuario(userId);
                    if (res.ok) {
                        const inscricoes = await res.json();
                        if (Array.isArray(inscricoes)) {
                            const grupoId = inscricaoCreated?.id_grupo_pagamento;
                            const createdIds = Array.isArray(inscricaoCreated?.inscricoes)
                                ? inscricaoCreated.inscricoes.map(i => String(i.id))
                                : [];

                            const matching = inscricoes.filter(i => {
                                if (grupoId && i.id_grupo_pagamento === grupoId) return true;
                                if (createdIds.includes(String(i.id))) return true;
                                return false;
                            });

                            if (matching.length > 0 && matching.every(i => i.status === 'Inscrito')) {
                                setPagamentoConfirmado(true);
                                setInscricoesConfirmadas(matching);
                                const firstWithNum = matching.find(i => i.num_corredor);
                                if (firstWithNum?.num_corredor) {
                                    setNumCorredor(firstWithNum.num_corredor);
                                }
                            }
                        }
                    }
                } catch (err) {
                    console.log('Erro ao consultar status da inscrição:', err);
                }
            };

            checkStatus();
            intervalId = setInterval(checkStatus, 4000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [showPagamento, hasVoucher, pagamentoConfirmado, inscricaoCreated]);

    const showError = (message) => {
        toast.current?.show({ severity: 'error', summary: 'Atenção', detail: message, life: 5000 });
    };

    // Manipulação de Terceiros
    const handleAdicionarTerceiro = () => {
        const vagasRestantes = loteAtivo?.vagas_disponiveis;
        const totalAtual = (incluirTitular ? 1 : 0) + terceiros.length;
        if (vagasRestantes !== undefined && vagasRestantes !== null && totalAtual >= vagasRestantes) {
            showError(`O lote atual possui apenas ${vagasRestantes} vaga(s) restante(s).`);
            return;
        }

        setTerceiros(prev => [
            ...prev,
            {
                id: Date.now() + Math.random(),
                nome_corredor: "",
                cpf_corredor: "",
                telefone_corredor: "",
                data_nascimento_corredor: "",
                categoria: null,
                tamanho_blusa: null,
                equipe_patrocinio: ""
            }
        ]);
    };

    const handleRemoverTerceiro = (id) => {
        if (!incluirTitular && terceiros.length <= 1) {
            showError("É necessário manter pelo menos 1 participante na inscrição.");
            return;
        }
        setTerceiros(prev => prev.filter(t => t.id !== id));
    };

    const handleAtualizarTerceiro = (id, campo, valor) => {
        setTerceiros(prev => prev.map(t => {
            if (t.id === id) {
                return { ...t, [campo]: valor };
            }
            return t;
        }));
    };

    // Categorias formatadas com identificador de 50% OFF 60+
    const formatCategoriaLabel = (option) => {
        if (!option) return '';
        const nome = option.categoria || option.nome_categoria || '';
        const faixa = option.faixa_etaria || (option.idade_minima !== undefined && option.idade_maxima !== undefined ? `${option.idade_minima} a ${option.idade_maxima} anos` : '');
        const temDesconto60 = !produto?.is_gratuita && isFaixaEtaria60Mais(option.faixa_etaria);
        const base = faixa ? `${nome} - ${faixa}` : nome;
        return temDesconto60 ? `${base} (50% OFF 60+)` : base;
    };

    const categoriasOptions = (categorias || []).map(cat => ({
        ...cat,
        displayLabel: formatCategoriaLabel(cat)
    }));

    // Participantes para cálculo dinâmico de subtotal
    const precoBaseLote = loteAtivo ? Number(loteAtivo.valor) : Number(produto?.valor_inscricao || 0);

    const listaParticipantesCalculo = [];
    if (incluirTitular) {
        listaParticipantesCalculo.push({
            is_terceiro: false,
            id_categoria_corrida: titularCategoria?.id,
            categoria: titularCategoria,
            nome: [userData.first_name, userData.last_name].filter(Boolean).join(" ") || "Você (Titular)"
        });
    }
    terceiros.forEach((t, i) => {
        listaParticipantesCalculo.push({
            is_terceiro: true,
            id_categoria_corrida: t.categoria?.id,
            categoria: t.categoria,
            nome: t.nome_corredor.trim() || `Terceiro #${i + 1}`
        });
    });

    const subtotalCalculado = calcularSubtotalParticipantes(
        listaParticipantesCalculo,
        precoBaseLote,
        categorias,
        Boolean(produto?.is_gratuita),
        Boolean(hasVoucher)
    );

    // Validações e envio do pedido
    async function handleSubmit() {
        setErrorMessage(null);

        const totalParticipantes = (incluirTitular ? 1 : 0) + terceiros.length;
        if (totalParticipantes === 0) {
            showError("Adicione pelo menos 1 participante para realizar a inscrição.");
            return;
        }

        // Validação do Titular se selecionado
        if (incluirTitular) {
            if (!titularCategoria) {
                showError("Por favor, selecione a categoria do Titular.");
                return;
            }
            if (!titularTamanhoBlusa) {
                showError("Por favor, selecione o tamanho da blusa do Titular.");
                return;
            }
        }

        // Checagem prévia de duplicidade de CPFs informados
        const cpfsUtilizados = [];
        if (incluirTitular && userData?.cpf) {
            cpfsUtilizados.push(unformatCPF(userData.cpf));
        }

        for (let i = 0; i < terceiros.length; i++) {
            const cpfClean = unformatCPF(terceiros[i].cpf_corredor);
            if (cpfClean) {
                if (cpfsUtilizados.includes(cpfClean)) {
                    showError(`O CPF ${formatCPF(cpfClean)} foi informado mais de uma vez nesta inscrição.`);
                    return;
                }
                cpfsUtilizados.push(cpfClean);
            }
        }

        // Validação dos dados de cada terceiro
        for (let i = 0; i < terceiros.length; i++) {
            const t = terceiros[i];
            const num = i + 1;
            const nomeTrim = t.nome_corredor ? t.nome_corredor.trim() : "";
            if (!nomeTrim || nomeTrim.split(/\s+/).length < 2) {
                showError(`Participante #${num}: Informe o nome completo (nome e sobrenome).`);
                return;
            }

            const cpfClean = unformatCPF(t.cpf_corredor);
            if (!cpfClean || !isValidCPF(cpfClean)) {
                showError(`Participante #${num} (${t.nome_corredor}): CPF inválido.`);
                return;
            }

            const telClean = unformatCPF(t.telefone_corredor);
            if (!telClean || telClean.length < 10) {
                showError(`Participante #${num}: Informe um telefone com DDD válido.`);
                return;
            }

            if (!t.data_nascimento_corredor) {
                showError(`Participante #${num}: Informe a data de nascimento.`);
                return;
            }

            const dtNasc = new Date(t.data_nascimento_corredor);
            const hoje = new Date();
            if (isNaN(dtNasc.getTime()) || dtNasc >= hoje) {
                showError(`Participante #${num}: Data de nascimento inválida.`);
                return;
            }

            if (!t.categoria) {
                showError(`Participante #${num}: Selecione uma categoria.`);
                return;
            }

            if (!t.tamanho_blusa) {
                showError(`Participante #${num}: Selecione o tamanho da blusa.`);
                return;
            }
        }

        // Validação de Voucher
        if (hasVoucher && !voucher.trim()) {
            showError("Para inscrições com cortesia, informe o código do voucher.");
            return;
        }

        // Validação de Lote
        if (!produto?.is_gratuita && !hasVoucher && !loteAtivo && !produto?.lote_ativo && !produto?.valor_inscricao) {
            showError("Não há lotes com vagas disponíveis para inscrição no momento.");
            return;
        }

        if (!produto?.is_gratuita && loteAtivo?.vagas_disponiveis !== undefined && loteAtivo?.vagas_disponiveis !== null) {
            if (totalParticipantes > loteAtivo.vagas_disponiveis) {
                showError(`O lote '${loteAtivo.nome_lote}' possui apenas ${loteAtivo.vagas_disponiveis} vaga(s) restante(s).`);
                return;
            }
        }

        // Montagem do payload de envio em lote
        const participantesPayload = [];

        if (incluirTitular) {
            participantesPayload.push({
                is_terceiro: false,
                id_categoria_corrida: titularCategoria.id,
                tamanho_blusa: titularTamanhoBlusa.Tamanho || titularTamanhoBlusa,
                equipe_patrocinio: titularEquipe || ""
            });
        }

        terceiros.forEach(t => {
            participantesPayload.push({
                is_terceiro: true,
                id_categoria_corrida: t.categoria.id,
                tamanho_blusa: t.tamanho_blusa.Tamanho || t.tamanho_blusa,
                equipe_patrocinio: t.equipe_patrocinio || "",
                nome_corredor: t.nome_corredor.trim(),
                cpf_corredor: unformatCPF(t.cpf_corredor),
                telefone_corredor: unformatCPF(t.telefone_corredor),
                data_nascimento_corredor: t.data_nascimento_corredor
            });
        });

        const metodoPagamento = produto?.is_gratuita ? "GRATUITO" : (hasVoucher ? "CORTESIA" : "PIX");

        const payload = {
            id_corrida: produto.id,
            metodo_pagamento: metodoPagamento,
            nome_voucher: hasVoucher ? voucher.trim() : null,
            participantes: participantesPayload
        };

        setShowForm(false);
        setShowEsqueleton(true);

        try {
            const response = await inscricaoService.setNovaInscricaoLote(payload);
            const dat = await response.json();

            if (response.status === 201) {
                // Guarda identificador para tracking e checkout
                const idRef = dat.id_grupo_pagamento || dat.id_requisicao || dat.inscricoes?.[0]?.id;
                sessionStorage.setItem('brv-id-inscricao', idRef);
                sessionStorage.setItem('brv-id-corrida', produto.id);

                setInscricaoCreated(dat);
                setShowEsqueleton(false);

                if (dat.status === 'Inscrito') {
                    setPagamentoConfirmado(true);
                    setInscricoesConfirmadas(dat.inscricoes || []);
                    setShowPagamento(true);
                    return;
                }

                setShowPagamento(true);
            } else {
                let errMsg = "Não foi possível realizar a inscrição.";
                if (dat?.erro) errMsg = dat.erro;
                else if (dat?.detail) errMsg = dat.detail;
                else if (dat?.mensagem) errMsg = dat.mensagem;
                else if (Array.isArray(dat?.participantes)) {
                    errMsg = dat.participantes.join(" | ");
                } else if (typeof dat?.participantes === 'object') {
                    errMsg = Object.values(dat.participantes).flat().join(" | ");
                }

                setShowEsqueleton(false);
                setShowForm(true);
                setErrorMessage(errMsg);
                showError(errMsg);
            }
        } catch (err) {
            console.error("Erro ao enviar inscrição em lote:", err);
            setShowEsqueleton(false);
            setShowForm(true);
            showError("Erro de conexão ao realizar a inscrição. Tente novamente.");
        }
    }

    const esqueleto = () => {
        return (
            <div className="flex flex-col gap-4 py-6">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-12 w-48" />
            </div>
        );
    };

    const cardPagamento = () => {
        const isInscritoOuGratuita = produto?.is_gratuita || inscricaoCreated?.status === 'Inscrito' || pagamentoConfirmado;
        const inscritosList = (inscricoesConfirmadas.length > 0 ? inscricoesConfirmadas : inscricaoCreated?.inscricoes) || [];

        return (
            <div className="mt-4">
                {isInscritoOuGratuita ? (
                    <div className="flex flex-col justify-center gap-5 p-6 bg-urb-card border border-urb-border rounded-xl shadow-lg text-left">
                        <div className="flex items-center text-urb-primary font-bold text-lg sm:text-xl gap-3">
                            <CheckCircle2 className="w-8 h-8 shrink-0 text-urb-primary" />
                            <span>
                                {hasVoucher
                                    ? "Inscrição com Voucher realizada com sucesso!"
                                    : produto?.is_gratuita
                                        ? "Inscrições confirmadas com sucesso!"
                                        : "Pagamento confirmado com sucesso!"}
                            </span>
                        </div>
                        <p className="text-slate-300 text-sm m-0">
                            Todas as inscrições deste pedido foram confirmadas! O comprovante com QR Code de cada participante foi enviado para o seu e-mail e também pode ser consultado no menu Minhas Corridas.
                        </p>

                        {/* Lista de Atletas Confirmados com Número de Peito */}
                        {inscritosList.length > 0 && (
                            <div className="flex flex-col gap-2.5 pt-2 border-t border-urb-border">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                    Atletas Inscritos ({inscritosList.length}):
                                </span>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {inscritosList.map((atleta, idx) => {
                                        const nomeAtleta = atleta.nome_corredor || [atleta.first_name, atleta.last_name].filter(Boolean).join(" ") || `Atleta #${idx + 1}`;
                                        return (
                                            <div key={atleta.id || idx} className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 flex items-center justify-between gap-3">
                                                <div className="flex flex-col min-w-0">
                                                    <span className="font-bold text-white text-sm truncate">{nomeAtleta}</span>
                                                    <span className="text-xs text-slate-400 truncate">{atleta.categoria || atleta.faixa_etaria || "Categoria Geral"}</span>
                                                    {atleta.is_terceiro && (
                                                        <span className="text-[10px] text-urb-primary font-semibold">Inscrição para Terceiro</span>
                                                    )}
                                                </div>
                                                {atleta.num_corredor ? (
                                                    <div className="flex flex-col items-end shrink-0">
                                                        <span className="text-[10px] text-slate-400 font-semibold uppercase">Nº Peito</span>
                                                        <span className="bg-urb-primary text-black font-mono font-black text-sm px-2.5 py-0.5 rounded-md">
                                                            {atleta.num_corredor}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs font-semibold text-emerald-400 shrink-0">Confirmado</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="pt-2">
                            <Button
                                variant="secondary"
                                onClick={() => window.location.href = "/minhas-corridas"}
                                className="flex items-center gap-2 border-urb-primary/40 text-urb-primary hover:bg-urb-primary/10"
                            >
                                <FileText className="w-4 h-4" />
                                Ir para Minhas Corridas
                            </Button>
                        </div>
                    </div>
                ) : (
                    <CheckoutPage
                        inscricaoId={inscricaoCreated?.id_grupo_pagamento || inscricaoCreated?.id_requisicao || sessionStorage.getItem('brv-id-inscricao')}
                        valorTotal={inscricaoCreated?.valor_total || subtotalCalculado.valorTotal}
                        nomeCorrida={produto.nome_corrida}
                        inscricoesLote={inscricaoCreated?.inscricoes}
                        onSuccess={() => setPagamentoConfirmado(true)}
                    />
                )}
            </div>
        );
    };

    const form = () => {
        const nomeCompletoTitular = [userData.first_name, userData.last_name].filter(Boolean).join(" ");

        return (
            <div className="flex flex-col gap-6 mt-4 text-left">
                {/* Cabeçalho da Lista de Participantes */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-3 border-b border-urb-border">
                    <div>
                        <h3 className="text-lg font-bold text-white flex items-center gap-2 m-0">
                            <Users className="w-5 h-5 text-urb-primary" />
                            Participantes da Corrida
                        </h3>
                        <p className="text-xs text-slate-400 m-0 mt-0.5">
                            Você pode inscrever a si mesmo e adicionar amigos ou familiares no mesmo pedido.
                        </p>
                    </div>

                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={handleAdicionarTerceiro}
                        className="flex items-center gap-1.5 font-bold text-xs border-urb-primary/40 text-urb-primary hover:bg-urb-primary/10"
                    >
                        <Plus className="w-4 h-4" />
                        Adicionar outro participante
                    </Button>
                </div>

                {/* Card 1: Titular (Usuário Logado) */}
                <div className={`p-5 rounded-2xl border transition-all ${
                    incluirTitular
                        ? "bg-urb-card border-urb-border"
                        : "bg-slate-950/40 border-slate-800 opacity-60"
                }`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center text-urb-primary">
                                <User className="w-4 h-4" />
                            </div>
                            <div>
                                <span className="font-bold text-white text-sm block">
                                    Minha Inscrição (Titular)
                                </span>
                                <span className="text-xs text-slate-400">
                                    {nomeCompletoTitular || "Usuário da Conta"}
                                </span>
                            </div>
                        </div>

                        {jaInscritoTitular ? (
                            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 flex items-center gap-1.5">
                                <Info className="w-3.5 h-3.5" />
                                Você já está inscrito
                            </span>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="check-titular"
                                    checked={incluirTitular}
                                    onChange={(e) => {
                                        if (!e.checked && terceiros.length === 0) {
                                            showError("Adicione um terceiro antes de remover sua inscrição, ou mantenha sua inscrição ativa.");
                                            return;
                                        }
                                        setIncluirTitular(e.checked);
                                    }}
                                />
                                <label htmlFor="check-titular" className="text-xs font-semibold text-slate-300 cursor-pointer select-none">
                                    Vou participar desta corrida
                                </label>
                            </div>
                        )}
                    </div>

                    {jaInscritoTitular && (
                        <div className="mb-4 p-3 rounded-xl bg-slate-900 border border-slate-800 text-xs text-slate-300 flex items-start gap-2">
                            <Info className="w-4 h-4 text-urb-primary shrink-0 mt-0.5" />
                            <span>
                                Identificamos que você já possui uma inscrição nesta corrida. Você pode inscrever amigos ou familiares utilizando o botão <strong>"+ Adicionar outro participante"</strong>.
                            </span>
                        </div>
                    )}

                    {incluirTitular && (
                        <div className="flex flex-col gap-4 pt-2 border-t border-urb-border">
                            {/* Dados do Usuário Titular (Somente Leitura) */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-400 mb-1 block">CPF</label>
                                    <Input value={formatCPF(userData.cpf || "")} disabled className="opacity-75 cursor-not-allowed bg-slate-900/60 text-xs" />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Telefone</label>
                                    <Input value={formatPhone(userData.telefone1 || "")} disabled className="opacity-75 cursor-not-allowed bg-slate-900/60 text-xs" />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-400 mb-1 block">E-mail</label>
                                    <Input value={userData.email || ""} disabled className="opacity-75 cursor-not-allowed bg-slate-900/60 text-xs" />
                                </div>
                            </div>

                            {/* Categoria, Blusa e Equipe do Titular */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Categoria *</label>
                                    <Select
                                        options={categoriasOptions}
                                        optionLabel="displayLabel"
                                        placeholder="Escolha uma categoria"
                                        value={titularCategoria}
                                        onChange={(e) => setTitularCategoria(e.value)}
                                    />
                                </div>

                                <div>
                                    <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Tamanho da Blusa *</label>
                                    <Select
                                        options={blusas}
                                        optionLabel="Tamanho"
                                        placeholder="Escolha o tamanho"
                                        value={titularTamanhoBlusa}
                                        onChange={(e) => setTitularTamanhoBlusa(e.value)}
                                    />
                                </div>

                                <div>
                                    <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Equipe / Patrocínio</label>
                                    <Input
                                        placeholder="Assessoria ou equipe (opcional)"
                                        value={titularEquipe}
                                        onChange={(e) => setTitularEquipe(e.target.value)}
                                        className="text-xs"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Cards de Terceiros */}
                {terceiros.map((terceiro, index) => (
                    <div key={terceiro.id} className="p-5 rounded-2xl border border-urb-border bg-urb-card flex flex-col gap-4 relative">
                        <div className="flex items-center justify-between pb-3 border-b border-urb-border">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400">
                                    <Users className="w-4 h-4" />
                                </div>
                                <div>
                                    <span className="font-bold text-white text-sm block">
                                        Participante #{index + 1} (Terceiro)
                                    </span>
                                    <span className="text-xs text-slate-400">
                                        Amigo, familiar ou membro da equipe
                                    </span>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => handleRemoverTerceiro(terceiro.id)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                                title="Remover este participante"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Dados Pessoais do Terceiro */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="sm:col-span-2">
                                <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Nome Completo *</label>
                                <Input
                                    placeholder="Nome e sobrenome do atleta"
                                    value={terceiro.nome_corredor}
                                    onChange={(e) => handleAtualizarTerceiro(terceiro.id, "nome_corredor", e.target.value)}
                                    className="text-xs"
                                />
                            </div>

                            <div>
                                <label className="text-[11px] font-semibold text-slate-400 mb-1 block">CPF *</label>
                                <Input
                                    placeholder="000.000.000-00"
                                    value={terceiro.cpf_corredor}
                                    maxLength={14}
                                    onChange={(e) => handleAtualizarTerceiro(terceiro.id, "cpf_corredor", formatCPF(e.target.value))}
                                    className="text-xs"
                                />
                            </div>

                            <div>
                                <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Telefone / WhatsApp *</label>
                                <Input
                                    placeholder="(00) 00000-0000"
                                    value={terceiro.telefone_corredor}
                                    maxLength={15}
                                    onChange={(e) => handleAtualizarTerceiro(terceiro.id, "telefone_corredor", formatPhone(e.target.value))}
                                    className="text-xs"
                                />
                            </div>

                            <div>
                                <label htmlFor={`data-nascimento-${terceiro.id}`} className="text-[11px] font-semibold text-slate-400 mb-1 block">Data de Nascimento *</label>
                                <Input
                                    id={`data-nascimento-${terceiro.id}`}
                                    type="date"
                                    value={terceiro.data_nascimento_corredor}
                                    onChange={(e) => handleAtualizarTerceiro(terceiro.id, "data_nascimento_corredor", e.target.value)}
                                    className="text-xs"
                                />
                            </div>

                            <div>
                                <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Categoria *</label>
                                <Select
                                    options={categoriasOptions}
                                    optionLabel="displayLabel"
                                    placeholder="Escolha uma categoria"
                                    value={terceiro.categoria}
                                    onChange={(e) => handleAtualizarTerceiro(terceiro.id, "categoria", e.value)}
                                />
                            </div>

                            <div>
                                <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Tamanho da Blusa *</label>
                                <Select
                                    options={blusas}
                                    optionLabel="Tamanho"
                                    placeholder="Escolha o tamanho"
                                    value={terceiro.tamanho_blusa}
                                    onChange={(e) => handleAtualizarTerceiro(terceiro.id, "tamanho_blusa", e.value)}
                                />
                            </div>

                            <div>
                                <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Equipe / Patrocínio</label>
                                <Input
                                    placeholder="Assessoria ou equipe (opcional)"
                                    value={terceiro.equipe_patrocinio}
                                    onChange={(e) => handleAtualizarTerceiro(terceiro.id, "equipe_patrocinio", e.target.value)}
                                    className="text-xs"
                                />
                            </div>
                        </div>
                    </div>
                ))}

                {/* Cupom / Voucher Promocional */}
                {!produto?.is_gratuita && (
                    <div className="flex flex-col gap-3 p-4 rounded-xl bg-slate-900/60 border border-urb-border">
                        <div className="flex items-center gap-3">
                            <Checkbox
                                id="hasVoucher"
                                checked={hasVoucher}
                                onChange={(e) => setHasVoucher(e.checked)}
                            />
                            <label htmlFor="hasVoucher" className="text-sm font-medium text-slate-300 cursor-pointer select-none">
                                Possuo um Cupom / Voucher Promocional de Cortesia
                            </label>
                        </div>

                        {hasVoucher && (
                            <div className="max-w-md pt-1">
                                <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Código do Voucher *</label>
                                <Input
                                    placeholder="Insira o código do voucher"
                                    value={voucher}
                                    onChange={(e) => setVoucher(e.target.value.toUpperCase())}
                                    className="uppercase font-mono font-bold"
                                />
                                <span className="text-[11px] text-slate-500 mt-1 block">
                                    O voucher deve possuir saldo suficiente para cobrir todos os participantes selecionados ({subtotalCalculado.totalParticipantes}).
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {errorMessage && (
                    <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{errorMessage}</span>
                    </div>
                )}

                {/* Resumo do Pedido e Subtotal em Tempo Real */}
                <div className="p-5 rounded-2xl bg-urb-card border border-urb-border flex flex-col gap-4 shadow-xl">
                    <div className="flex items-center justify-between pb-3 border-b border-urb-border">
                        <span className="text-sm font-bold text-white flex items-center gap-2">
                            <Ticket className="w-4 h-4 text-urb-primary" />
                            Resumo do Pedido ({subtotalCalculado.totalParticipantes} participante{subtotalCalculado.totalParticipantes > 1 ? "s" : ""})
                        </span>
                        <span className="text-xs font-semibold text-slate-400">
                            {loteAtivo?.nome_lote || produto?.lote_ativo?.nome_lote || (produto?.is_gratuita ? "Evento Gratuito" : "Lote Padrão")}
                        </span>
                    </div>

                    {/* Detalhamento de cada participante */}
                    <div className="flex flex-col gap-2">
                        {subtotalCalculado.detalhamento.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs py-1 text-slate-300">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-white">{item.nome}</span>
                                    {item.is60Mais && (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                            50% OFF (60+)
                                        </span>
                                    )}
                                </div>
                                <span className="font-bold text-white">
                                    R$ {item.valorFinal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </div>
                        ))}
                    </div>

                    <Divider className="my-1" />

                    {/* Total Geral */}
                    <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                            <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Total a Pagar</span>
                            <span className="text-xs text-slate-500">
                                {produto?.is_gratuita ? "Inscrição gratuita" : hasVoucher ? "Cortesia via voucher" : "Pagamento único consolidado"}
                            </span>
                        </div>
                        <span className="text-2xl sm:text-3xl font-black text-urb-primary">
                            R$ {subtotalCalculado.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                    </div>
                </div>

                {/* Botão de Envio */}
                <div className="pt-2">
                    <Button
                        onClick={() => handleSubmit()}
                        disabled={subtotalCalculado.totalParticipantes === 0}
                        className="w-full sm:w-auto px-8 py-3.5 font-bold text-base shadow-neon flex items-center justify-center gap-2"
                    >
                        <Check className="w-5 h-5" />
                        {produto?.is_gratuita
                            ? "Confirmar Inscrições Gratuitas"
                            : hasVoucher
                                ? "Confirmar Inscrições com Voucher"
                                : "Avançar para Pagamento"}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full">
            <Toast ref={toast} />
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">{produto?.nome_corrida}</h2>
            {showForm && form()}
            {showEsqueleton && esqueleto()}
            {showPagamento && cardPagamento()}
        </div>
    );
}

export default InscricaoCorredor;
